import { useEffect } from 'react';
import SidebarLayout from 'layouts/SidebarLayout';
import Sidescreen from 'components/Sidescreen';
import { profile } from 'lang/en.json';
import styles from 'styles/modules/mediaCoverage/Index.module.scss';
import Button from 'components/Button';
import { useDispatch, useSelector } from 'react-redux';
import EmptyBlock from 'components/EmptyBlock';

import {
    handleHidePopup,
    handleHideSidescreen,
    handleShowPopup,
    handleShowSidescreen,
    setPopupConfig,
    setPopupLoading,
} from 'store/ux';
import {
    fetchCoverages,
    pinCoverage,
    removeCoverage,
    seeCoverages,
} from 'store/company/coverage';
import Card from 'components/Card';
import { companySelector, fetchCompanyBySlug } from 'store/company';
import SEO from 'next-seo.config';
import { NextSeo } from 'next-seo';
import { wrapper } from 'store';
import auth from 'utils/auth';
import Typo from 'components/Typo';

export default function Coverages({ company, isAuthorized, isAuthenticated }) {
    const {
        data: { name, slug: companySlug },
    } = useSelector(companySelector);
    const coverages = useSelector(({ company }) => company.coverages.data);
    const dispatch = useDispatch();

    const handleAddCoverage = () => {
        dispatch(handleShowSidescreen('add-mediaCoverage'));
    };

    const newCoverageButton = isAuthorized && (
        <Button
            onClick={handleAddCoverage}
            classes={[styles.button]}
            title={profile.media_coverage.button}
        />
    );

    const handleRemovePopup = (id) => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: () => handleRemoveConfirm(id),
                onCancel: handleRemoveCancel,
                title: 'Delete Media coverage?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const handleRemoveConfirm = async (id) => {
        dispatch(setPopupLoading(true));
        await dispatch(removeCoverage(id));
        dispatch(setPopupLoading(false));
        dispatch(handleHidePopup());
    };

    const handleRemoveCancel = () => {
        dispatch(handleHidePopup());
    };

    const handlePin = (id) => {
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                onConfirm: () => handlePinConfirm(id),
                onCancel: handleRemoveCancel,
                title: 'Change pin status of the media coverage?',
                confirmTitle: 'Confirm',
                cancelTitle: 'Cancel',
            })
        );
    };

    const handlePinConfirm = async (id) => {
        dispatch(setPopupLoading(true));
        await dispatch(pinCoverage(id));
        dispatch(setPopupLoading(false));
        dispatch(handleHidePopup());
    };

    const published = coverages?.length > 0 && (
        <>
            <div className={styles.cardBlocks}>
                {coverages.map((item, key) => (
                    <Card
                        title={item.title}
                        body={item.description}
                        key={`SingleCard-item-${key}`}
                        link={item.url}
                        onRemove={isAuthorized && handleRemovePopup}
                        cta={'Read more'}
                        id={item.id}
                        limit={200}
                        status={item.status}
                        onApprove={
                            companySlug === company && isAuthorized && handlePin
                        }
                    />
                ))}
            </div>
        </>
    );

    const handleMarkCoveragesAsSeen = async () => {
        await dispatch(seeCoverages());
    };

    useEffect(() => {
        if (
            isAuthenticated &&
            company === companySlug &&
            coverages?.some(({ status }) => status === 'new')
        ) {
            handleMarkCoveragesAsSeen();
        }
    }, []);

    const isEmpty = coverages?.length < 1;

    return (
        <>
            <NextSeo {...SEO} titleTemplate={`${name} Media Coverage | %s`} />
            <SidebarLayout isAuthenticated={isAuthenticated}>
                <div className={styles.wrapper}>
                    <div className={['mb-l', styles.titleWrapper].join(' ')}>
                        <Typo variant={'h1'}>
                            {isAuthorized
                                ? profile.media_coverage.title
                                : `${name}'s Media Coverage`}
                        </Typo>
                        {newCoverageButton}
                    </div>
                    {published}
                    {isEmpty && (
                        <EmptyBlock
                            title={profile.media_coverage.placeholder_empty}
                        />
                    )}
                </div>
                <Sidescreen
                    closeFn={handleHideSidescreen}
                    onUpdate={() => {
                        dispatch(handleHideSidescreen());
                    }}
                />
            </SidebarLayout>
        </>
    );
}

export const getServerSideProps = wrapper.getServerSideProps(
    (store) =>
        async ({ req, res, params }) => {
            try {
                await store.dispatch(fetchCompanyBySlug(params.company));
            } catch (err) {
                return {
                    notFound: true,
                };
            }
            await auth(store, req, res, params);
            await store.dispatch(fetchCoverages());
            return {
                props: {
                    data: store.getState().company.coverages.data,
                    isAuthorized: store.getState().auth.isAuthorized,
                    isAuthenticated: store.getState().auth.isAuthenticated,
                    company: store.getState().auth.company,
                },
            };
        }
);
